import { Link } from "react-router-dom";
import { useGame, useLiveGameTime } from "../app/GameContext";
import { TECHNOLOGIES, TECHNOLOGY_BY_ID } from "../game-data/technologies";
import { technologyLevelLabel } from "../game-data/terminology";
import { isDiplomacyUnlocked } from "../game-engine/diplomacy";
import {
  EmptyState,
  Grade,
  LiveGameTime,
  Meter,
  PageHeader,
  formatDeadline,
  formatNumber,
  labels,
  progressBetween,
} from "../components/GameUi";

const CAMPAIGN_SYSTEM_COUNT = 5;

export function OverviewPage() {
  const { state, secondsPerGameYear } = useGame();
  const liveGameTime = useLiveGameTime();
  if (!state) return null;
  const levels = state.player.levels;
  const currentResearch = state.technologies.current;
  const activeMissions = state.missions.filter((mission) => mission.status === "enRoute" || mission.status === "returning");
  const homeBodyIds = new Set(state.systems.find((system) => system.distanceLy === 0)?.bodies.map((body) => body.id) ?? []);
  const offHomeColonies = Object.values(state.developments).filter((development) => (
    !homeBodyIds.has(development.bodyId)
    && development.level >= 5
    && (development.installations.colony ?? 0) > 0
  )).length;
  const observedSystems = state.systems.filter((system) => system.observed).length;
  const campaignComplete = state.technologies.completed.length >= TECHNOLOGIES.length
    && observedSystems >= CAMPAIGN_SYSTEM_COUNT
    && offHomeColonies >= 3;
  const diplomacyUnlocked = isDiplomacyUnlocked(state);
  const contactedCivilization = state.civilizations.some((civilization) => civilization.contacted);
  const nextUnlock = levels.industry < 2
    ? { title: "资源调度", detail: "完成工业能力 2级研究后，开放能源保障顺序与挂机自动化。", to: "../technology", action: "前往科技树" }
    : !diplomacyUnlocked
      ? { title: "星际外交", detail: "完成一次存在人工信号的系外恒星系轨道勘测后开放。", to: "../star-map", action: "查看星图" }
      : !contactedCivilization
        ? { title: "贸易与战斗", detail: "与一个系外文明建立正式接触后，开放贸易合同与舰队战斗工作区。", to: "../diplomacy", action: "建立接触" }
        : null;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow={<>COMMAND OVERVIEW // <LiveGameTime compact /></>}
        title={state.player.civilizationName}
        description="各星球独立生产和储存能源、工程材料；科研与远程任务由服务器按真实经过时间结算。"
        actions={<button className="button" onClick={() => window.location.reload()}>重新同步</button>}
      />

      <section aria-labelledby="resource-heading">
        <div className="section-heading"><div><p className="eyebrow">CAPACITY</p><h2 id="resource-heading">核心资源</h2></div><Link to="../resources">完整资源表 →</Link></div>
        <div className="metric-grid">
          <article className="metric-card resource-card tone-energy" data-tone="energy">
            <span>各星球能源合计</span><strong>{formatNumber(state.resources.energy.available)}</strong>
            <Meter value={state.resources.energy.available} max={state.resources.energy.capacity} label="各星球能源合计" tone="energy" />
            <small>年产 {signed(state.resources.energy.productionPerYear)} · 维护 −{formatNumber(state.resources.energy.maintenancePerYear)}</small>
          </article>
          <article className="metric-card resource-card tone-materials" data-tone="materials">
            <span>各星球材料合计</span><strong>{formatNumber(Object.values(state.resources.materials).reduce((sum, value) => sum + value, 0))}</strong>
            <Meter value={Object.values(state.resources.materials).reduce((sum, value) => sum + value, 0)} max={state.resources.materialCapacity} label="各星球材料合计" tone="materials" />
            <small>跨星球建设必须先安排运输</small>
          </article>
          <article className="metric-card resource-card tone-research" data-tone="research">
            <span>科研能力</span><strong>{formatNumber(state.resources.research)}</strong>
            <Meter value={state.resources.research} max={state.resources.researchCapacity} label="科研库存" tone="research" />
            <small>年产 {signed(state.resources.researchPerYear)}</small>
          </article>
        </div>
      </section>

      {nextUnlock && (
        <section className="panel unlock-preview" aria-labelledby="next-unlock-heading">
          <div><p className="eyebrow">NEXT SYSTEM</p><h2 id="next-unlock-heading">下一项功能：{nextUnlock.title}</h2><p>{nextUnlock.detail}</p></div>
          <Link className="button" to={nextUnlock.to}>{nextUnlock.action} →</Link>
        </section>
      )}

      <details className={`panel overview-archive ${campaignComplete ? "campaign-complete" : ""}`}>
        <summary>
          <span><b>文明档案与长期目标</b><small>查看六项能力等级及多恒星文明完成条件</small></span>
          <span className="status-chip">{campaignComplete ? "文明工程完成" : "持续推进"}</span>
        </summary>
        <div className="overview-archive-content">
          <section aria-labelledby="level-heading">
            <div className="section-heading"><div><p className="eyebrow">CIVILIZATION PROFILE</p><h2 id="level-heading">文明能力等级</h2></div></div>
            <div className="grade-grid">
              <Grade label={labels.levels.civilization} value={`${levels.civilization}级`} />
              <Grade label={labels.levels.industry} value={`${levels.industry}级`} />
              <Grade label={labels.levels.navigation} value={`${levels.navigation}级`} />
              <Grade label={labels.levels.military} value={`${levels.military}级`} />
              <Grade label={labels.levels.energy} value={`${levels.energy}级`} />
              <Grade label={labels.levels.materials} value={`${levels.materials}级`} />
            </div>
          </section>
          <section aria-labelledby="campaign-heading">
            <div className="section-heading">
              <div><p className="eyebrow">LONG HORIZON</p><h2 id="campaign-heading">多恒星文明长期目标</h2></div>
            </div>
            <div className="milestone-grid">
              <div><span>科技体系</span><b>{state.technologies.completed.length} / {TECHNOLOGIES.length}</b><Meter value={state.technologies.completed.length} max={TECHNOLOGIES.length} label="科技体系进度" /></div>
              <div><span>恒星系观测</span><b>{observedSystems} / {CAMPAIGN_SYSTEM_COUNT}</b><Meter value={observedSystems} max={CAMPAIGN_SYSTEM_COUNT} label="恒星系观测进度" /></div>
              <div><span>系外永久殖民地</span><b>{offHomeColonies} / 3</b><Meter value={offHomeColonies} max={3} label="系外殖民进度" /></div>
            </div>
            <p className="field-hint">完成全部科技、观测全部恒星系，并在母星系外建立 3 个永久殖民地，才算建立可持续的多恒星文明。</p>
          </section>
        </div>
      </details>

      <div className="split-grid">
        <section className="panel" aria-labelledby="project-heading">
          <div className="section-heading"><div><p className="eyebrow">ACTIVE PROJECT</p><h2 id="project-heading">当前研究</h2></div><Link to="../technology">科技树 →</Link></div>
          {currentResearch ? (
            <div className="progress-item">
              <div className="progress-heading">
                <span><strong>{technologyLevelLabel(currentResearch.techId)} · {TECHNOLOGY_BY_ID[currentResearch.techId]?.name}</strong><small>已投入 {formatNumber(currentResearch.invested)} 科研点</small></span>
                <b>{Math.round(progressBetween(currentResearch.startedAtGameYear, currentResearch.completesAtGameYear, liveGameTime) * 100)}%</b>
              </div>
              <Meter value={progressBetween(currentResearch.startedAtGameYear, currentResearch.completesAtGameYear, liveGameTime)} max={1} label="当前研究进度" tone="research" />
              <small>{formatDeadline(currentResearch.completesAtGameYear, liveGameTime, secondsPerGameYear)}</small>
            </div>
          ) : <EmptyState>研究队列空闲。前往科技树安排下一项研究。</EmptyState>}
        </section>
        <section className="panel" aria-labelledby="mission-heading">
          <div className="section-heading"><div><p className="eyebrow">MISSIONS</p><h2 id="mission-heading">远程任务</h2></div><Link to="../fleet">舰队控制 →</Link></div>
          {activeMissions.length ? (
            <>
              <ul className="progress-list">{activeMissions.slice(0, 4).map((mission) => {
                const progress = progressBetween(mission.launchedAtGameYear, mission.arrivesAtGameYear, liveGameTime);
                return (
                  <li key={mission.id}>
                    <div className="progress-heading"><span><b>{labels.missions[mission.type]}</b><small>{mission.shipIds.length} 艘飞船 · {labels.status[mission.status]}</small></span><b>{Math.round(progress * 100)}%</b></div>
                    <Meter value={progress} max={1} label={`${labels.missions[mission.type]}任务进度`} />
                    <small>{formatDeadline(mission.arrivesAtGameYear, liveGameTime, secondsPerGameYear)}</small>
                  </li>
                );
              })}</ul>
              {activeMissions.length > 4 && <p className="field-hint">另有 {activeMissions.length - 4} 项航行任务，可在舰队控制中查看。</p>}
            </>
          ) : <EmptyState>当前没有航行中的任务。</EmptyState>}
        </section>
      </div>

      {state.events.pending.length > 0 && <section className="panel attention" aria-labelledby="alert-heading">
        <div className="section-heading"><div><p className="eyebrow">ALERT QUEUE</p><h2 id="alert-heading">待处理警报</h2></div><Link to="../events">事件日志 →</Link></div>
        {state.events.pending.length ? (
          <ul className="data-list">{state.events.pending.map((event) => <li key={event.id}><span><b>{event.title}</b><small>{event.description}</small></span><span>需要决策</span></li>)}</ul>
        ) : <EmptyState>没有需要立即确认的重大事件。</EmptyState>}
      </section>}
    </div>
  );
}

function signed(value: number) {
  return `${value >= 0 ? "+" : ""}${formatNumber(value)}`;
}
